import React, { useEffect, useMemo, useState } from "react";
import {
    MapPin,
    Plus,
    Edit3,
    Trash2,
    RefreshCw,
    Loader2,
    Search,
    X,
    Save,
    Eye,
    EyeOff,
    Compass,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
    LOCATION_TYPE_OPTIONS,
    LOCATION_TYPE_META,
    LINE_OPTIONS,
    LINE_META,
} from "../../lib/locations";

const EMPTY_FORM = {
    name: "",
    type: "cafe",
    address: "",
    city: "",
    postal_code: "",
    country: "Portugal",
    lat: "",
    lng: "",
    phone: "",
    hours: "",
    website: "",
    photo_url: "",
    lines: [],
    notes: "",
    active: true,
};

export default function AdminLocations({ apiAuth }) {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(true);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null); // null | "new" | locationObj

    const load = async () => {
        setBusy(true);
        try {
            const res = await apiAuth("/admin/locations");
            setItems(res.data || []);
        } catch {
            toast.error("Erro ao carregar pontos de venda");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((l) =>
            `${l.name} ${l.address} ${l.city}`.toLowerCase().includes(q),
        );
    }, [items, search]);

    const activeCount = useMemo(
        () => items.filter((l) => l.active).length,
        [items],
    );

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Apagar definitivamente "${name}"?`)) return;
        try {
            await apiAuth(`/admin/locations/${id}`, { method: "DELETE" });
            toast.success("Ponto de venda apagado");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro a apagar");
        }
    };

    const handleToggleActive = async (loc) => {
        try {
            await apiAuth(`/admin/locations/${loc.id}`, {
                method: "PUT",
                data: { active: !loc.active },
            });
            toast.success(
                !loc.active ? "Ponto ativado" : "Ponto desativado (oculto no site)",
            );
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-locations-panel">
            {/* Top stats + actions */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="rounded-3xl bg-white p-5 shadow-soft ring-1 ring-brand-green/5">
                    <MapPin className="h-5 w-5 text-brand-green" />
                    <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-brand-ink-soft">
                        Total de pontos
                    </div>
                    <div className="mt-1 font-display font-medium text-3xl text-brand-ink">
                        {items.length}
                    </div>
                </div>
                <div className="rounded-3xl bg-brand-green-soft p-5 shadow-soft ring-1 ring-brand-green/5">
                    <Eye className="h-5 w-5 text-brand-green" />
                    <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-brand-ink-soft">
                        Ativos no site
                    </div>
                    <div className="mt-1 font-display font-medium text-3xl text-brand-green">
                        {activeCount}
                    </div>
                </div>
                <div className="rounded-3xl bg-brand-orange-soft p-5 shadow-soft ring-1 ring-brand-green/5">
                    <EyeOff className="h-5 w-5 text-brand-orange" />
                    <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-brand-ink-soft">
                        Ocultos
                    </div>
                    <div className="mt-1 font-display font-medium text-3xl text-brand-orange">
                        {items.length - activeCount}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setEditing("new")}
                    data-testid="btn-new-location"
                    className="rounded-3xl bg-brand-green hover:bg-brand-green-dark text-white p-5 shadow-pop transition-colors flex flex-col items-start justify-between text-left"
                >
                    <Plus className="h-5 w-5" />
                    <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-white/85">
                        Adicionar
                    </div>
                    <div className="mt-1 font-display font-medium text-2xl">
                        Novo ponto
                    </div>
                </button>
            </section>

            {/* Search bar */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px] max-w-xl">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink-soft" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Pesquisar pontos…"
                        data-testid="admin-locations-search"
                        className="w-full rounded-full bg-white border-2 border-brand-green/15 focus:border-brand-green px-10 py-2.5 text-sm font-medium text-brand-ink placeholder:text-brand-ink-soft focus:outline-none transition-colors"
                    />
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors ml-auto"
                    data-testid="admin-locations-refresh"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                    Atualizar
                </button>
            </div>

            {/* List */}
            <section className="rounded-3xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden">
                {busy ? (
                    <div className="p-16 flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <MapPin className="h-10 w-10 text-brand-green/40 mx-auto" />
                        <p className="mt-4 text-sm text-brand-ink-soft">
                            {items.length === 0
                                ? "Ainda não há pontos de venda. Adiciona o primeiro!"
                                : "Nenhum ponto encontrado para esta pesquisa."}
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-brand-green/10">
                        {filtered.map((loc) => {
                            const meta = LOCATION_TYPE_META[loc.type];
                            return (
                                <li
                                    key={loc.id}
                                    data-testid={`admin-location-${loc.id}`}
                                    className="px-5 py-4 hover:bg-brand-cream/40 transition-colors flex items-start md:items-center gap-3 md:gap-5 flex-col md:flex-row"
                                >
                                    <div className="flex items-start gap-3 flex-1 min-w-0 w-full">
                                        <div className="h-11 w-11 rounded-full bg-brand-green-soft text-brand-green flex items-center justify-center text-lg shrink-0">
                                            {meta?.emoji || "📍"}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-bold text-brand-ink">
                                                    {loc.name}
                                                </span>
                                                {!loc.active && (
                                                    <span className="text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-brand-ink/10 text-brand-ink-soft inline-flex items-center gap-1">
                                                        <EyeOff className="h-2.5 w-2.5" />
                                                        Oculto
                                                    </span>
                                                )}
                                                <span className="text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-brand-cream text-brand-ink">
                                                    {meta?.label || loc.type}
                                                </span>
                                                {(loc.lines || []).map((l) => (
                                                    <span
                                                        key={l}
                                                        className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${LINE_META[l]?.color || ""}`}
                                                    >
                                                        {LINE_META[l]?.label || l}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="mt-1 text-xs text-brand-ink-soft truncate">
                                                {loc.address} ·{" "}
                                                <span className="font-semibold text-brand-ink">
                                                    {loc.city}
                                                </span>
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-brand-ink-soft">
                                                {loc.lat.toFixed(4)},{" "}
                                                {loc.lng.toFixed(4)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
                                        <button
                                            type="button"
                                            onClick={() => handleToggleActive(loc)}
                                            data-testid={`btn-toggle-${loc.id}`}
                                            title={
                                                loc.active
                                                    ? "Ocultar do site"
                                                    : "Mostrar no site"
                                            }
                                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                                                loc.active
                                                    ? "bg-brand-green-soft text-brand-green hover:bg-brand-green hover:text-white"
                                                    : "bg-brand-cream text-brand-ink hover:bg-brand-ink hover:text-white"
                                            }`}
                                        >
                                            {loc.active ? (
                                                <Eye className="h-3 w-3" />
                                            ) : (
                                                <EyeOff className="h-3 w-3" />
                                            )}
                                            {loc.active ? "Ativo" : "Oculto"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setEditing(loc)}
                                            data-testid={`btn-edit-${loc.id}`}
                                            className="inline-flex items-center gap-1 rounded-full bg-white hover:bg-brand-green-soft border border-brand-green/15 text-brand-ink px-3 py-1.5 text-xs font-semibold transition-colors"
                                        >
                                            <Edit3 className="h-3 w-3" /> Editar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleDelete(loc.id, loc.name)
                                            }
                                            data-testid={`btn-delete-${loc.id}`}
                                            className="inline-flex items-center gap-1 rounded-full bg-white hover:bg-red-500 border border-red-200 hover:border-red-500 text-red-500 hover:text-white px-3 py-1.5 text-xs font-semibold transition-colors"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {editing && (
                <LocationDialog
                    apiAuth={apiAuth}
                    initial={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}

// ============================================================
// Create / Edit dialog
// ============================================================
function LocationDialog({ apiAuth, initial, onClose, onSaved }) {
    const isNew = !initial;
    const [form, setForm] = useState(() =>
        initial
            ? {
                  ...EMPTY_FORM,
                  ...initial,
                  lat: initial.lat ?? "",
                  lng: initial.lng ?? "",
                  lines: initial.lines || [],
              }
            : { ...EMPTY_FORM },
    );
    const [saving, setSaving] = useState(false);
    const [geocoding, setGeocoding] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const toggleLine = (k) =>
        setForm((f) => ({
            ...f,
            lines: f.lines.includes(k)
                ? f.lines.filter((x) => x !== k)
                : [...f.lines, k],
        }));

    const handleGeocode = async () => {
        const query = [form.address, form.postal_code, form.city, form.country]
            .filter(Boolean)
            .join(", ");
        if (query.length < 5) {
            toast.error("Preenche pelo menos morada e cidade.");
            return;
        }
        setGeocoding(true);
        try {
            const res = await apiAuth("/admin/geocode", {
                method: "POST",
                data: { query },
            });
            set("lat", res.data.lat);
            set("lng", res.data.lng);
            toast.success("Coordenadas obtidas: " + res.data.display_name);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Não consegui geocodificar");
        } finally {
            setGeocoding(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const lat = parseFloat(form.lat);
        const lng = parseFloat(form.lng);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            toast.error("Coordenadas inválidas. Usa o botão 'Obter coordenadas' ou insere manualmente.");
            return;
        }
        const payload = {
            name: form.name.trim(),
            type: form.type,
            address: form.address.trim(),
            city: form.city.trim(),
            postal_code: form.postal_code?.trim() || null,
            country: form.country?.trim() || "Portugal",
            lat,
            lng,
            phone: form.phone?.trim() || null,
            hours: form.hours?.trim() || null,
            website: form.website?.trim() || null,
            photo_url: form.photo_url?.trim() || null,
            lines: form.lines,
            notes: form.notes?.trim() || null,
            active: !!form.active,
        };
        setSaving(true);
        try {
            if (isNew) {
                await apiAuth("/admin/locations", {
                    method: "POST",
                    data: payload,
                });
                toast.success("Ponto de venda criado");
            } else {
                await apiAuth(`/admin/locations/${initial.id}`, {
                    method: "PUT",
                    data: payload,
                });
                toast.success("Ponto de venda atualizado");
            }
            await onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro a guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="location-dialog"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={handleSubmit}
                className="w-full max-w-3xl my-8 rounded-3xl bg-white shadow-2xl overflow-hidden"
            >
                <div className="px-6 py-4 bg-brand-green text-white flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        <h2 className="font-display font-medium text-lg">
                            {isNew ? "Novo ponto de venda" : "Editar ponto"}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/15 transition-colors"
                        data-testid="dialog-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
                    {/* name + type */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Nome *">
                            <input
                                required
                                type="text"
                                value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                                placeholder="Ex: Café Central"
                                className={inputCls}
                                data-testid="loc-field-name"
                            />
                        </Field>
                        <Field label="Tipo *">
                            <select
                                value={form.type}
                                onChange={(e) => set("type", e.target.value)}
                                className={inputCls}
                                data-testid="loc-field-type"
                            >
                                {LOCATION_TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.emoji} {o.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    {/* address */}
                    <Field label="Morada *">
                        <input
                            required
                            type="text"
                            value={form.address}
                            onChange={(e) => set("address", e.target.value)}
                            placeholder="Rua / Avenida / Nº"
                            className={inputCls}
                            data-testid="loc-field-address"
                        />
                    </Field>
                    <div className="grid md:grid-cols-3 gap-4">
                        <Field label="Cidade *">
                            <input
                                required
                                type="text"
                                value={form.city}
                                onChange={(e) => set("city", e.target.value)}
                                className={inputCls}
                                data-testid="loc-field-city"
                            />
                        </Field>
                        <Field label="Código postal">
                            <input
                                type="text"
                                value={form.postal_code}
                                onChange={(e) => set("postal_code", e.target.value)}
                                placeholder="0000-000"
                                className={inputCls}
                                data-testid="loc-field-postal"
                            />
                        </Field>
                        <Field label="País">
                            <input
                                type="text"
                                value={form.country}
                                onChange={(e) => set("country", e.target.value)}
                                className={inputCls}
                                data-testid="loc-field-country"
                            />
                        </Field>
                    </div>

                    {/* coords + geocode */}
                    <div className="rounded-2xl bg-brand-cream/60 p-4">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-green mb-3">
                            <Compass className="h-3.5 w-3.5" /> Coordenadas
                        </div>
                        <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                            <Field label="Latitude *">
                                <input
                                    required
                                    type="number"
                                    step="any"
                                    value={form.lat}
                                    onChange={(e) => set("lat", e.target.value)}
                                    placeholder="38.7110"
                                    className={inputCls}
                                    data-testid="loc-field-lat"
                                />
                            </Field>
                            <Field label="Longitude *">
                                <input
                                    required
                                    type="number"
                                    step="any"
                                    value={form.lng}
                                    onChange={(e) => set("lng", e.target.value)}
                                    placeholder="-9.1370"
                                    className={inputCls}
                                    data-testid="loc-field-lng"
                                />
                            </Field>
                            <button
                                type="button"
                                onClick={handleGeocode}
                                disabled={geocoding}
                                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark disabled:opacity-50 text-white px-4 py-2.5 text-xs font-bold transition-colors"
                                data-testid="loc-btn-geocode"
                            >
                                {geocoding ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Compass className="h-3.5 w-3.5" />
                                )}
                                Obter da morada
                            </button>
                        </div>
                        <p className="mt-2 text-[11px] text-brand-ink-soft flex items-start gap-1.5">
                            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0 text-brand-orange" />
                            Preenche a morada e cidade primeiro, depois clica em
                            "Obter da morada". Geocoding gratuito via OpenStreetMap.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Telefone">
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                                placeholder="+351 ..."
                                className={inputCls}
                                data-testid="loc-field-phone"
                            />
                        </Field>
                        <Field label="Horário">
                            <input
                                type="text"
                                value={form.hours}
                                onChange={(e) => set("hours", e.target.value)}
                                placeholder="Seg-Sáb 09:00-19:00"
                                className={inputCls}
                                data-testid="loc-field-hours"
                            />
                        </Field>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Website">
                            <input
                                type="url"
                                value={form.website}
                                onChange={(e) => set("website", e.target.value)}
                                placeholder="https://..."
                                className={inputCls}
                                data-testid="loc-field-website"
                            />
                        </Field>
                        <Field label="URL da foto">
                            <input
                                type="url"
                                value={form.photo_url}
                                onChange={(e) => set("photo_url", e.target.value)}
                                placeholder="https://..."
                                className={inputCls}
                                data-testid="loc-field-photo"
                            />
                        </Field>
                    </div>

                    <Field label="Linhas disponíveis">
                        <div className="flex flex-wrap gap-2">
                            {LINE_OPTIONS.map((o) => {
                                const active = form.lines.includes(o.value);
                                return (
                                    <button
                                        type="button"
                                        key={o.value}
                                        onClick={() => toggleLine(o.value)}
                                        data-testid={`loc-line-${o.value}`}
                                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-all ${
                                            active
                                                ? `${LINE_META[o.value]?.color} ring-2 ring-offset-1 ring-brand-green`
                                                : "bg-brand-cream text-brand-ink-soft hover:bg-brand-green-soft"
                                        }`}
                                    >
                                        {o.label}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>

                    <Field label="Notas (interno)">
                        <textarea
                            rows={2}
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            placeholder="Notas visíveis no popup do mapa…"
                            className={`${inputCls} resize-none`}
                            data-testid="loc-field-notes"
                        />
                    </Field>

                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!form.active}
                            onChange={(e) => set("active", e.target.checked)}
                            className="h-4 w-4 rounded border-brand-green/30 text-brand-green focus:ring-brand-green"
                            data-testid="loc-field-active"
                        />
                        <span className="text-sm font-semibold text-brand-ink">
                            Ativo (visível no site público)
                        </span>
                    </label>
                </div>

                <div className="px-6 py-4 bg-brand-cream/60 border-t border-brand-green/10 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-cream border border-brand-green/15 text-brand-ink px-4 py-2 text-xs font-semibold transition-colors"
                        data-testid="dialog-cancel"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark disabled:opacity-50 text-white px-5 py-2 text-xs font-bold transition-colors"
                        data-testid="dialog-save"
                    >
                        {saving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Save className="h-3.5 w-3.5" />
                        )}
                        {isNew ? "Criar ponto" : "Guardar alterações"}
                    </button>
                </div>
            </form>
        </div>
    );
}

const inputCls =
    "w-full rounded-xl bg-white border-2 border-brand-green/15 focus:border-brand-green px-3 py-2 text-sm font-medium text-brand-ink placeholder:text-brand-ink-soft focus:outline-none transition-colors";

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-widest font-bold text-brand-ink-soft">
            {label}
        </span>
        <div className="mt-1.5">{children}</div>
    </label>
);
